import { normalizeEntityType } from '@/shared/lib/asset';

import { EXPERIMENT_RELATED_TYPES, RUN_SUB_ASSET_TYPES } from '@/entities/asset-search/domain/constants';
import type { AssetSearchResultItem } from '@/entities/asset-search/domain/types';
import type { SearchResult } from '@/entities/search/domain/types';

import { buildTierInfo } from './tierHelper';

const appendSnapshotParams = (params: URLSearchParams, normalizedType: string, item: AssetSearchResultItem) => {
  if (normalizedType !== 'dataset' && normalizedType !== 'feature_set') return;

  const snapshotId = normalizedType === 'dataset' ? item.dataset?.snapshotId : item.featureSet?.snapshotId;

  if (snapshotId != null) {
    params.set('snapshotId', String(snapshotId));
  }
};

const buildExperimentPath = (item: AssetSearchResultItem): string => {
  const projectId = item.path.find((p) => p.type === 'project')?.id || item.project?.id;
  const experimentId = item.path.find((p) => p.type === 'experiment')?.id || item.experiment?.id;

  if (!projectId || !experimentId) {
    return '';
  }

  if (item.type === 'run') {
    const params = new URLSearchParams({ assetType: 'run' });
    return `/experiments/${projectId}/${experimentId}/${item.id}?${params.toString()}`;
  }

  if (RUN_SUB_ASSET_TYPES.some((t) => t === item.type)) {
    const runId = item.run?.id;
    if (!runId) {
      return '';
    }
    const normalizedType = item.type.replace('run_', '');
    const params = new URLSearchParams({ assetType: normalizedType });
    appendSnapshotParams(params, normalizedType, item);

    const querySuffix = params.toString() ? `?${params.toString()}` : '';

    return `/experiments/${projectId}/${experimentId}/${runId}/${item.id}${querySuffix}`;
  }

  return `/experiments/${projectId}/${experimentId}`;
};

export const mapAssetSearchResultToSearchResult = (item: AssetSearchResultItem): SearchResult => {
  const isExperimentRelated = EXPERIMENT_RELATED_TYPES.includes(item.type);
  const pathString = isExperimentRelated
    ? buildExperimentPath(item)
    : item.path.length > 0
      ? item.path[item.path.length - 1].to || ''
      : '';

  const projectItem = item.path.find((p) => p.type === 'project');
  const experimentItem = item.path.find((p) => p.type === 'experiment');

  const experimentData = {
    ...(projectItem && { projectId: String(projectItem.id) }),
    ...(experimentItem && {
      experimentId: String(experimentItem.id),
      experimentName: experimentItem.label,
    }),
  };

  const normalizedType = normalizeEntityType(item.type);

  const baseResult: SearchResult = {
    id: item.id.toString(),
    title: item.title,
    description: item.description || '',
    type: normalizedType as SearchResult['type'],
    updatedAt: item.createdAt,
    owner: {
      name: '',
    },
    path: pathString,
    tags: [],
    tier: item.featureSet?.tier && item.featureSet.tier !== null ? buildTierInfo(item.featureSet.tier) : undefined,
    _source: {
      assetPath: item.path,
      rawAssetItem: item,
    } as SearchResult['_source'],
  };

  if (item.type === 'experiment' && item.experiment) {
    return {
      ...baseResult,
      ...experimentData,
    };
  }

  if (item.type === 'run' && item.run) {
    return {
      ...baseResult,
      ...experimentData,
      createdAt: item.run.startTime,
    };
  }

  if ((item.type === 'feature_set' || item.type === 'run_feature_set') && item.featureSet) {
    return {
      ...baseResult,
      ...experimentData,
      tier: buildTierInfo(item.featureSet.tier),
    };
  }

  if ((item.type === 'dataset' || item.type === 'run_dataset') && item.dataset) {
    return {
      ...baseResult,
      ...experimentData,
      description: item.description || '',
      tier: buildTierInfo(item.dataset.tier),
    };
  }

  if (item.type === 'run_parameter_set' || item.type === 'run_artifact') {
    return {
      ...baseResult,
      ...experimentData,
      description: item.description || '',
    };
  }

  return baseResult;
};
